import React from "react";
import { Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import KpiCard from "./dashboard/KpiCard";
import UsersOverview from "./dashboard/UsersOverview";
import MostViewedVideos from "./dashboard/MostViewedVideos";
import TopGenres from "./dashboard/TopGenres";
import StoreCard from "./dashboard/StoreCard";
import PlatformShare from "./dashboard/PlatformShare";
import StoreDownloads from "./dashboard/StoreDownloads";
import { formatNumber } from "../../../utils/numbers";
import { TrendChip } from "./dashboard/ActiveUsersTrend";
import {
  combineStoreStats,
  useStoreStats,
  useUserCounts,
} from "./dashboard/useDashboardData";
import "./dashboard/dashboard.css";

const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.afo.media";
const APP_STORE_URL = "https://apps.apple.com/app/id6466264011";

const display = (value) => (value === null ? "—" : formatNumber(value));

const Dashbord = () => {
  const { t, i18n } = useTranslation();
  const userCounts = useUserCounts();
  const stores = useStoreStats();
  const apps = combineStoreStats([stores.ios, stores.android]);
  const today = new Date().toLocaleDateString(i18n.language, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  return (
    <Container fluid className="dash">
      <header className="dash-header">
        <div>
          <h1 className="dash-title">{t("Dashboard")}</h1>
          <p className="dash-subtitle">
            {t("Overview of your platform activity")}
          </p>
        </div>
        <span className="dash-date">{today}</span>
      </header>

      <div className="dash-grid dash-kpis">
        <KpiCard
          icon="las la-download"
          color="var(--dash-blue)"
          label={t("Total downloads")}
          value={display(apps.downloads)}
          hint={t("iOS and Android")}
          loading={stores.loading}
        />
        <KpiCard
          icon="las la-chart-line"
          color="var(--dash-green)"
          label={t("Downloads, last 30 days")}
          value={
            apps.downloadsLast30Days === null
              ? "—"
              : `+${formatNumber(apps.downloadsLast30Days)}`
          }
          hint={t("New installs on both stores")}
          loading={stores.loading}
        />
        <KpiCard
          icon="las la-mobile-alt"
          color="var(--dash-violet)"
          label={t("Active devices")}
          value={display(apps.activeDevices)}
          trend={
            <TrendChip
              change={apps.activeDevicesChange}
              title={t("vs the previous 30 days")}
            />
          }
          hint={
            apps.activeDevicesChange === null
              ? t("Last 30 days")
              : t("Last 30 days, vs the previous 30 days")
          }
          loading={stores.loading}
        />
        <KpiCard
          icon="las la-star"
          color="var(--dash-amber)"
          label={t("Average rating")}
          value={apps.rating ? `${apps.rating.toFixed(1)} / 5` : "—"}
          hint={
            apps.reviews === null
              ? undefined
              : t("reviewsCount", {
                  count: apps.reviews,
                  formatted: formatNumber(apps.reviews),
                })
          }
          loading={stores.loading}
        />
      </div>

      <h2 className="dash-section-title">{t("Mobile apps")}</h2>
      <div className="dash-grid dash-stores">
        <StoreCard
          name="App Store"
          icon="lab la-apple"
          color="var(--dash-blue)"
          url={APP_STORE_URL}
          stats={stores.ios}
          loading={stores.loading}
        />
        <StoreCard
          name="Play Store"
          icon="lab la-google-play"
          color="var(--dash-green)"
          url={PLAY_STORE_URL}
          stats={stores.android}
          loading={stores.loading}
        />
        <PlatformShare
          ios={stores.ios}
          android={stores.android}
          loading={stores.loading}
        />
      </div>

      <h2 className="dash-section-title">{t("Platform")}</h2>
      <div className="dash-grid dash-main">
        <UsersOverview counts={userCounts} />
        <MostViewedVideos />
      </div>

      <TopGenres />

      <StoreDownloads />
    </Container>
  );
};

export default Dashbord;
